import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Clock,
  BookOpen,
  ArrowRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { skillsAssessmentService, LearningPlan } from '../../services/skillsAssessment';
import { CourseRecommendationCard } from './CourseRecommendationCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SubscriptionTier } from '../../types';
import { ROUTES } from '../../utils/constants';

interface LearningPlanDisplayProps {
  assessmentId?: string;
  planId?: string;
  className?: string;
}

const LearningPlanDisplay: React.FC<LearningPlanDisplayProps> = ({
  assessmentId,
  planId,
  className = '',
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [learningPlan, setLearningPlan] = useState<LearningPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removedCourses, setRemovedCourses] = useState<Set<string>>(new Set());

  const loadLearningPlan = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let plan: LearningPlan;
      if (planId) {
        plan = await skillsAssessmentService.getLearningPlan(planId);
      } else if (assessmentId) {
        plan = await skillsAssessmentService.generateLearningPlan(assessmentId);
      } else {
        plan = await skillsAssessmentService.getLearningPlan();
      }

      setLearningPlan(plan);
    } catch (err) {
      console.error('Error loading learning plan:', err);
      setError('Failed to load learning plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [assessmentId, planId]);

  useEffect(() => {
    if (user) {
      loadLearningPlan();
    }
  }, [loadLearningPlan, user]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Not set';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return 'Not set';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'Not set';
    }
  };

  const getTotalProgress = () => {
    if (!learningPlan?.recommendedCourses) return 0;
    const totalMilestones  = learningPlan.recommendedCourses.reduce((s, c) => s + (c.milestones?.length || 0), 0);
    const completedMilestones = learningPlan.recommendedCourses.reduce((s, c) => s + (c.milestones?.filter(m => m.isCompleted).length || 0), 0);
    return totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;
  };

  const handleCourseLike = (courseId: string) => {
    setRemovedCourses(prev => { const s = new Set(prev); s.delete(courseId); return s; });
  };

  const handleCourseDislike = async (courseId: string) => {
    if (!learningPlan) return;
    try {
      setRemovedCourses(prev => new Set(prev).add(courseId));
      const result = await skillsAssessmentService.dislikeCourseAndReplace(courseId, learningPlan.planId);
      if (result.success && result.replacementCourse) {
        setLearningPlan(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            recommendedCourses: prev.recommendedCourses.map(course => {
              if (course.courseId === courseId) return { ...course, status: 'disliked' as const };
              if (course.courseId === result.replacementCourse?.courseId) return result.replacementCourse as any;
              return course;
            }),
          };
        });
        setRemovedCourses(new Set());
      }
    } catch (err) {
      console.error('Error disliking course:', err);
      setRemovedCourses(prev => { const s = new Set(prev); s.delete(courseId); return s; });
      setError('Failed to update course preference. Please try again.');
    }
  };

  const handleStartCourse = (courseId: string, moduleId?: string) => {
    navigate(moduleId ? `/courses/${courseId}/modules/${moduleId}` : `/courses/${courseId}`);
  };

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        {/* Loading header */}
        <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Building Your Learning Plan</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            We're analysing your skills and matching you with the best courses. This usually takes 10–20 seconds.
          </p>
        </div>

        {/* Skeleton course cards */}
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-lg p-5 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadLearningPlan}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!learningPlan) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 ${className}`}>
        <p className="text-gray-600 text-center">No learning plan available.</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-gray-900">Your Personalised Learning Plan</h2>
            <p className="text-gray-600">{learningPlan.overallSummary}</p>
            <div className="flex flex-col gap-2 text-sm text-gray-600 sm:flex-row sm:items-center sm:space-x-6 sm:gap-0">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(learningPlan.startDate)} – {formatDate(learningPlan.endDate)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <BookOpen className="w-4 h-4" />
                <span>{learningPlan.recommendedCourses?.length || 0} Courses</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>{learningPlan.durationWeeks || 4} Weeks</span>
              </div>
            </div>
            <div className="text-left sm:hidden">
              <div className="text-3xl font-bold text-blue-600">{Math.round(getTotalProgress())}%</div>
              <p className="text-sm text-gray-600">Overall Progress</p>
            </div>
          </div>
          <div className="hidden text-right sm:block">
            <div className="text-3xl font-bold text-blue-600">{Math.round(getTotalProgress())}%</div>
            <p className="text-sm text-gray-600">Overall Progress</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-teal-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getTotalProgress()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Course list */}
      <div className="grid gap-6">
        {(learningPlan.recommendedCourses || [])
          .filter(course => course.status === 'active' && !removedCourses.has(course.courseId))
          .map((course, index) => (
            <CourseRecommendationCard
              key={course.courseId}
              course={course}
              index={index}
              onLike={handleCourseLike}
              onDislike={handleCourseDislike}
              onStartCourse={handleStartCourse}
            />
          ))}

        {learningPlan.recommendedCourses &&
          learningPlan.recommendedCourses.length > 0 &&
          removedCourses.size === learningPlan.recommendedCourses.length && (
            <div className="text-center py-8">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-lg text-gray-800 mb-2">No courses to show</h3>
              <p className="text-gray-600 mb-4">
                You've removed all recommended courses. Browse our catalogue to find alternatives.
              </p>
              <button
                onClick={() => navigate(ROUTES.courses)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all"
              >
                Browse Course Catalog
              </button>
            </div>
          )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-center space-x-4 pt-6 flex-wrap gap-y-4">
        <button
          onClick={() => {
            const isFree = !user || user.subscriptionTier === SubscriptionTier.FREE;
            navigate(isFree ? '/pricing' : '/dashboard');
          }}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all flex items-center space-x-2"
        >
          <BookOpen className="w-4 h-4" />
          <span>Start Learning</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => navigate(ROUTES.learningPlan)}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
        >
          <BookOpen className="w-4 h-4" />
          <span>View Full Learning Plan</span>
        </button>
      </div>
    </div>
  );
};

export default LearningPlanDisplay;
